/**
 * Model Configuration Loader
 * 
 * This module provides TypeScript types and helper functions for loading
 * and accessing the shared model configuration from the backend.
 * 
 * Requirements: 4.2, 4.5
 */

// Import the shared configuration file
import modelConfigData from '../../../../packages/shared-config/model-config.json';

/**
 * Represents a single model configuration entry
 */
export interface ModelConfig {
  customId: string;
  bedrockId?: string | null;
  isRouter?: boolean;
  routerCandidates?: string[];
  displayName: string;
  provider: string;
  modelFamily: string;
  description: string;
  capabilities: string[];
  defaultTokensPerCredit: number;
}

/**
 * Represents the complete model configuration file structure
 */
export interface ModelConfigFile {
  version: string;
  models: ModelConfig[];
}

/**
 * The loaded model configuration
 * @throws Error if configuration is missing or invalid
 */
export const modelConfig: ModelConfigFile = (() => {
  try {
    // Validate that the configuration was loaded
    if (!modelConfigData) {
      throw new Error('Model configuration file not found');
    }

    // Validate required fields
    if (!modelConfigData.version) {
      throw new Error('Model configuration missing version field');
    }

    if (!Array.isArray(modelConfigData.models)) {
      throw new Error('Model configuration missing or invalid models array');
    }

    // Validate each model has required fields (bedrockId is optional for router models)
    modelConfigData.models.forEach((model, index) => {
      const requiredFields = ['customId', 'displayName', 'provider'];
      const missingFields = requiredFields.filter(field => !model[field as keyof typeof model]);
      
      if (missingFields.length > 0) {
        throw new Error(
          `Model at index ${index} is missing required fields: ${missingFields.join(', ')}`
        );
      }
    });

    return modelConfigData as ModelConfigFile;
  } catch (error) {
    console.error('Failed to load model configuration:', error);
    throw error;
  }
})();

/**
 * Get all available models from the configuration
 * 
 * @returns Array of all model configurations
 */
export function getAvailableModels(): ModelConfig[] {
  return modelConfig.models;
}

/**
 * Get a specific model configuration by its custom ID
 * 
 * @param customId - The custom model ID to look up
 * @returns The model configuration if found, undefined otherwise
 */
export function getModelById(customId: string): ModelConfig | undefined {
  return modelConfig.models.find(m => m.customId === customId);
}

/**
 * Get the display name for a model by its custom ID
 * 
 * @param customId - The custom model ID to look up
 * @returns The display name if found, or the custom ID as fallback
 */
export function getModelDisplayName(customId: string): string {
  const model = getModelById(customId);
  return model?.displayName || customId;
}

/**
 * Check if a custom model ID is valid
 * 
 * @param customId - The custom model ID to validate
 * @returns True if the model ID exists in the configuration
 */
export function isValidModelId(customId: string): boolean {
  return modelConfig.models.some(m => m.customId === customId);
}

/**
 * Get all available custom model IDs
 * 
 * @returns Array of all custom model IDs
 */
export function getAvailableModelIds(): string[] {
  return modelConfig.models.map(m => m.customId);
}

/**
 * Get models filtered by provider
 * 
 * @param provider - The provider name to filter by
 * @returns Array of models from the specified provider
 */
export function getModelsByProvider(provider: string): ModelConfig[] {
  return modelConfig.models.filter(m => m.provider === provider);
}

/**
 * Get models filtered by model family
 * 
 * @param modelFamily - The model family to filter by
 * @returns Array of models from the specified family
 */
export function getModelsByFamily(modelFamily: string): ModelConfig[] {
  return modelConfig.models.filter(m => m.modelFamily === modelFamily);
}

/**
 * Get models filtered by capability
 * 
 * @param capability - The capability to filter by (e.g., "chat", "text-generation")
 * @returns Array of models that support the specified capability
 */
export function getModelsByCapability(capability: string): ModelConfig[] {
  return modelConfig.models.filter(m => m.capabilities.includes(capability));
}
